import { createAsyncThunk } from '@reduxjs/toolkit';
import resources from '../../resources';
import { type AccessToken } from '../../types/ApiService';
import { type AppState } from '../../types/store/states/AppState';
import ClientOAuth2 from 'client-oauth2';
import config from '../../services/config';

const client = new ClientOAuth2(config.auth);

export const getToken = createAsyncThunk(
  'auth/getToken',
  async (
    { username, password }: { username: string; password: string },
    { getState },
  ) => {
    const state = getState() as AppState;
    const usernameString = `FacilityUser|${state.common.facility!.id}|${username}`;

    return resources.auth
      .postToken(usernameString, password)
      .then((data) => client.createToken(data))
      .then((accessToken) => {
        return accessToken as unknown as AccessToken;
      });
  },
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (storeRedirectUrl: boolean = false) => {
    return storeRedirectUrl && window.location.pathname != '/login'
      ? window.location.pathname
      : '/';
  },
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (authToken: AccessToken, { getState }) => {
    const state = getState() as AppState;

    // Add the facility ID to the headers for cloud support
    const headers: Record<string, string> = {};
    if (state.common.facility) {
      headers['X-Facility-ID'] = String(state.common.facility.id);
    }

    const token = client.createToken(
      authToken.accessToken,
      authToken.refreshToken,
      authToken.tokenType,
      authToken.data as ClientOAuth2.Data,
    );
    token.expiresIn(new Date(authToken.expires));
    return token.refresh({ headers }).then((result) => {
      return result as unknown as AccessToken;
    });
  },
);

export const updateSignature = createAsyncThunk(
  'auth/updateSignature',
  async (signature: string, { getState }) => {
    const state = getState() as AppState;
    const facilityUser = state.auth.facilityUser;

    if (!facilityUser) {
      return Promise.reject(new Error('No facility user found'));
    }

    return resources.facilityUser.putSignature(facilityUser.id, signature);
  },
);
